import React from 'react';

import { Typography } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import ColoredButton from 'components/ColoredButton';
import type { OwnerWorkspaceAsset } from 'config/api/assetRopa/assetRopaWorkspace.types';
import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import OwnerWorkspaceProcessingTable from './OwnerWorkspaceProcessingTable';

type CompletenessMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_workspace.completeness_status;

type Props = {
  asset: OwnerWorkspaceAsset;
  onAddProcessingActivity: (asset: OwnerWorkspaceAsset) => void;
  onOpenEditor: (row: AssetProcessingActivityReviewRow, asset: OwnerWorkspaceAsset, mode: 'view' | 'edit') => void;
  onOpenDpia?: (row: AssetProcessingActivityReviewRow) => void;
  onRemoveProcessingActivity: (row: AssetProcessingActivityReviewRow, asset: OwnerWorkspaceAsset) => void;
};

const formatDate = (value: string) => {
  try {
    return format(new Date(value), 'yyyy-MM-dd');
  } catch {
    return value;
  }
};

const getCompletenessBadgeClass = (status: OwnerWorkspaceAsset['informationalCompleteness'], styles: ReturnType<typeof useStyles>) => {
  if (status === 'complete') {
    return styles.statusBadgeSuccess;
  }

  if (status === 'no_processing_activities') {
    return styles.statusBadgeInfo;
  }

  return styles.statusBadgeWarn;
};

const OwnerWorkspaceAssetPanel: React.FC<Props> = ({
  asset,
  onAddProcessingActivity,
  onOpenDpia,
  onOpenEditor,
  onRemoveProcessingActivity,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const panelMessages = processing_activities_messages.asset_ropa.owner_workspace.panel;
  const completenessKey = asset.informationalCompleteness as CompletenessMessageKey;

  const warningChips = [
    asset.warnings.sensitivePersonalData ? t(processing_activities_messages.asset_ropa.warnings.sensitive_personal_data) : null,
    asset.gdprRelevanceStatus === 'no_longer_indicated'
      ? t(processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance.no_longer_indicated)
      : null,
  ].filter(Boolean) as string[];

  return (
    <details className={styles.assetPanel}>
      <summary className={styles.assetSummary}>
        <div className={styles.assetSummaryMain}>
          <div className={styles.assetName}>{asset.assetName}</div>
          <div className={styles.assetMeta}>
            <span>
              {asset.assetTypeName} ·{' '}
              {t(panelMessages.updated, { date: formatDate(asset.lastUpdatedAt), user: asset.lastUpdatedByName ?? '—' })}
            </span>
          </div>
        </div>
        <div className={styles.workspaceSummaryGrid}>
          <div>
            <div className={styles.summaryLabel}>{t(panelMessages.operational_owner)}</div>
            <div className={styles.summaryValue}>
              {asset.operationalOwnerName ?? t(processing_activities_messages.asset_ropa.panel.not_assigned)}
            </div>
          </div>
          <div>
            <div className={styles.summaryLabel}>{t(panelMessages.processing_activities)}</div>
            <div className={styles.summaryValue}>{asset.processingActivitiesCount}</div>
          </div>
          <div>
            <span className={clsx(styles.statusBadge, getCompletenessBadgeClass(asset.informationalCompleteness, styles))}>
              {t(processing_activities_messages.asset_ropa.owner_workspace.completeness_status[completenessKey])}
            </span>
          </div>
          <div>
            {warningChips.map(chip => (
              <span key={chip} className={styles.warningChip}>
                {chip}
              </span>
            ))}
            {asset.gdprRelevanceStatus === 'currently_indicated' && (
              <span className={clsx(styles.statusBadge, styles.statusBadgeSuccess)}>
                {t(processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance.currently_indicated)}
              </span>
            )}
          </div>
        </div>
      </summary>
      <div className={styles.assetPanelContent}>
        {asset.processingActivityReviews.length === 0 ? (
          <div className={styles.emptyProcessingState}>
            <div>
              <Typography variant='h3'>{t(processing_activities_messages.asset_ropa.panel.no_processing_activities_title)}</Typography>
              <Typography color='textSecondary'>
                {t(processing_activities_messages.asset_ropa.panel.no_processing_activities_body)}
              </Typography>
            </div>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={false}
              icon={false}
              onClick={() => onAddProcessingActivity(asset)}
              showLoader={false}
              startIcon={<AddIcon />}
              textLike={false}
              variant='outlined'
            >
              {t(processing_activities_messages.asset_ropa.actions.add_processing_activity)}
            </ColoredButton>
          </div>
        ) : (
          <>
            <div className={styles.assetToolbar}>
              <div className={styles.assetToolbarNote}>{t(panelMessages.completeness_note)}</div>
              <ColoredButton
                className=''
                customColor='secondary'
                disabled={false}
                icon={false}
                onClick={() => onAddProcessingActivity(asset)}
                showLoader={false}
                startIcon={<AddIcon />}
                textLike={false}
                variant='outlined'
              >
                {t(processing_activities_messages.asset_ropa.actions.add_processing_activity)}
              </ColoredButton>
            </div>
            <OwnerWorkspaceProcessingTable
              onOpenDpia={onOpenDpia}
              onOpenEditor={(row, mode) => onOpenEditor(row, asset, mode)}
              onRemove={row => onRemoveProcessingActivity(row, asset)}
              rows={asset.processingActivityReviews}
            />
          </>
        )}
      </div>
    </details>
  );
};

export default OwnerWorkspaceAssetPanel;
